export interface PageRoute {
  component: string;
  routes: {
    en: string;
    sq: string;
    de: string;
    fr: string;
    nl?: string;
  };
}

export const SUPPORTED_LANGUAGES = ["en", "sq", "de", "fr", "nl"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_PREFIXES: Record<SupportedLanguage, string> = {
  en: "/support",
  sq: "/informacion",
  de: "/verordnung",
  fr: "/assistance",
  nl: "/ondersteuning",
};

export const PAGE_ROUTES: Record<string, PageRoute> = {
  childrenRules: {
    component: "InformationPage",
    routes: {
      en: "/support/what-rules-apply-to-children-traveling-with-airtiketa",
      sq: "/informacion/cfare-rregullash-zbatohen-per-femijet-qe-udhetojne-me-airtiketa",
      de: "/verordnung/welche-regeln-gelten-für-kinder-die-mit-airtiketa-reisen",
      fr: "/assistance/quelles-règles-sappliquent-aux-enfants-voyageant-avec-airtiketa",
      nl: "/ondersteuning/welke-regels-gelden-voor-kinderen-die-met-airtiketa-reizen",
    },
  },
  babyBaggage: {
    component: "BabyBaggageAllowance",
    routes: {
      en: "/support/does-my-baby-get-baggage-allowance",
      sq: "/informacion/a-ka-femija-im-te-drejte-per-bagazh",
      de: "/verordnung/hat-mein-baby-anspruch-auf-freigepäck",
      fr: "/assistance/mon-bébé-a-t-il-droit-à-une-franchise-bagage",
      nl: "/ondersteuning/krijgt-mijn-baby-bagageruimte",
    },
  },
  childTurnsTwo: {
    component: "ChildTurnsTwo",
    routes: {
      en: "/support/what-happens-if-my-baby-turns-2-during-the-trip",
      sq: "/informacion/cfare-ndodh-nese-femija-im-mbush-2-vjec-gjate-udhetimit",
      de: "/verordnung/was-passiert-wenn-mein-baby-während-der-reise-2-jahre-alt-wird",
      fr: "/assistance/que-se-passe-t-il-si-mon-bébé-a-2-ans-pendant-le-voyage",
      nl: "/ondersteuning/wat-gebeurt-er-als-mijn-baby-2-wordt-tijdens-de-reis",
    },
  },
  childTurnsTwelve: {
    component: "ChildTurnsTwelve",
    routes: {
      en: "/support/what-happens-if-my-child-turns-12-during-the-trip",
      sq: "/informacion/cfare-ndodh-nese-femija-im-mbush-12-vjec-gjate-udhetimit",
      de: "/verordnung/was-passiert-wenn-mein-kind-während-der-reise-12-jahre-alt-wird",
      fr: "/assistance/que-se-passe-t-il-si-mon-enfant-a-12-ans-pendant-le-voyage",
      nl: "/ondersteuning/wat-gebeurt-er-als-mijn-kind-12-wordt-tijdens-de-reis",
    },
  },
  childrenAlone: {
    component: "ChildrenAlone",
    routes: {
      en: "/support/can-children-under-18-fly-alone-with-airtiketa",
      sq: "/informacion/a-mund-te-udhetojne-femijet-nen-18-vjec-vetem-me-airtiketa",
      de: "/verordnung/dürfen-kinder-unter-18-jahren-alleine-mit-airtiketa-fliegen",
      fr: "/assistance/les-enfants-de-moins-de-18-ans-peuvent-ils-voyager-seuls-avec-airtiketa",
      nl: "/ondersteuning/kunnen-kinderen-onder-18-alleen-vliegen-met-airtiketa",
    },
  },
  childrenBreastfeeding: {
    component: "ChildrenBreastfeeding",
    routes: {
      en: "/support/can-i-breastfeed-or-bring-breast-milk-on-my-flight",
      sq: "/informacion/a-mund-te-ushqej-me-gji-ose-te-marr-qumesht-gjiri-ne-fluturim",
      de: "/verordnung/darf-ich-während-des-fluges-stillen-oder-muttermilch-mitnehmen",
      fr: "/assistance/puis-je-allaiter-ou-apporter-du-lait-maternel-sur-mon-vol",
      nl: "/ondersteuning/mag-ik-borstvoeding-geven-of-moedermelk-meenemen-op-mijn-vlucht",
    },
  },
  childrenStroller: {
    component: "ChildrenStroller",
    routes: {
      en: "/support/can-i-bring-a-stroller-on-board",
      sq: "/informacion/a-mund-te-marr-karrocen-e-femijes-ne-bord",
      de: "/verordnung/darf-ich-einen-kinderwagen-an-bord-mitnehmen",
      fr: "/assistance/puis-je-apporter-une-poussette-à-bord",
      nl: "/ondersteuning/mag-ik-een-kinderwagen-aan-boord-meenemen",
    },
  },
  childrenCarSeat: {
    component: "ChildrenCarSeat",
    routes: {
      en: "/support/can-i-place-my-child-in-a-car-seat-on-board",
      sq: "/informacion/a-mund-ta-vendos-femijen-ne-karrige-makine-ne-bord",
      de: "/verordnung/darf-ich-mein-kind-in-einem-kindersitz-an-bord-platzieren",
      fr: "/assistance/puis-je-installer-mon-enfant-dans-un-siège-auto-à-bord",
      nl: "/ondersteuning/mag-ik-mijn-kind-in-een-autostoeltje-aan-boord-plaatsen",
    },
  },
  childrenUnborn: {
    component: "ChildrenUnborn",
    routes: {
      en: "/support/how-do-i-make-a-booking-for-a-baby-that-isnt-born",
      sq: "/informacion/si-te-bej-rezervim-per-nje-foshnje-qe-nuk-ka-lindur-ende",
      de: "/verordnung/wie-buche-ich-einen-flug-für-ein-noch-ungeborenes-baby",
      fr: "/assistance/comment-faire-une-réservation-pour-un-bébé-pas-encore-né",
      nl: "/ondersteuning/hoe-maak-ik-een-boeking-voor-een-baby-die-nog-niet-geboren-is",
    },
  },
  childrenPregnancy: {
    component: "ChildrenPregnancy",
    routes: {
      en: "/support/traveling-while-pregnant-what-you-need-to-know",
      sq: "/informacion/udhetimi-gjate-shtatzenise-cfare-duhet-te-dini",
      de: "/verordnung/reisen-während-der-schwangerschaft-was-sie-wissen-müssen",
      fr: "/assistance/voyager-pendant-la-grossesse-ce-quil-faut-savoir",
      nl: "/ondersteuning/reizen-tijdens-zwangerschap-wat-u-moet-weten",
    },
  },
  childrenSeating: {
    component: "ChildrenSeating",
    routes: {
      en: "/support/will-my-child-get-their-own-seat",
      sq: "/informacion/a-do-te-kete-femija-im-vendin-e-vet-ne-avion",
      de: "/verordnung/bekommt-mein-kind-einen-eigenen-sitzplatz",
      fr: "/assistance/mon-enfant-aura-t-il-son-propre-siège",
      nl: "/ondersteuning/krijgt-mijn-kind-een-eigen-stoel",
    },
  },

  seatsV2BuyExtra: {
    component: "BuyExtraSeatPage",
    routes: {
      en: "/support/buy-an-extra-seat",
      sq: "/informacion/si-te-blej-nje-vend-shtese",
      de: "/verordnung/wie-kaufe-ich-einen-zusätzlichen-sitzplatz",
      fr: "/assistance/comment-acheter-un-siège-supplémentaire",
    },
  },
  seatsV2ReserveOnBoard: {
    component: "SeatReservationPage",
    routes: {
      en: "/support/reserve-a-seat-on-board",
      sq: "/informacion/si-te-rezervoj-nje-vend-ne-bord",
      de: "/verordnung/wie-reserviere-ich-einen-sitzplatz-an-bord",
      fr: "/assistance/comment-réserver-un-siège-à-bord",
    },
  },
  seatsV2ChildSeat: {
    component: "ChildrenSeatingPage",
    routes: {
      en: "/support/will-my-child-get-their-own-seat",
      sq: "/informacion/a-do-te-kete-femija-im-ulesen-e-vet",
      de: "/verordnung/bekommt-mein-kind-einen-eigenen-sitzplat",
      fr: "/assistance/mon-enfant-aura-t-il-son-propre-siège",
    },
  },
  seatsV2OptionsDifference: {
    component: "SeatSelectionGuidePage",
    routes: {
      en: "/support/whats-the-difference-between-seat-options",
      sq: "/informacion/cila-eshte-diferenca-mes-opsioneve-te-uleseve",
      de: "/verordnung/was-ist-der-unterschied-zwischen-den-sitzoptionen",
      fr: "/assistance/quelle-est-la-différence-entre-les-options-de-sièges",
    },
  },
  SpaceSeatInfoPage: {
    component: "SpaceSeatInfoPage",
    routes: {
      en: "/support/space-seat",
      sq: "/informacion/ulese-hapesire",
      de: "/verordnung/space-sitz",
      fr: "/assistance/siege-espace",
    },
  },
  SeatbeltExtensionPage: {
    component: "SeatbeltExtensionPage",
    routes: {
      en: "/support/seat-belt-extension",
      sq: "/informacion/zgjatje-rripi-sigurimi",
      de: "/verordnung/gurtverlangerung",
      fr: "/assistance/rallonge-ceinture-securite",
    },
  },
  baggageCheckIn: {
    component: "BaggageCheckInPage",
    routes: {
      en: "/support/how-much-luggage-can-i-check-in-for-my-flight",
      sq: "/informacion/sa-bagazh-mund-te-dorezoj-per-kontroll",
      de: "/verordnung/wie-viel-gepäck-kann-ich-für-meinen-flug-aufgeben",
      fr: "/assistance/combien-de-bagages-puis-je-enregistrer-pour-mon-vol",
    },
  },
  baggageDamagedOrDelayed: {
    component: "BaggageDamagedOrDelayedPage",
    routes: {
      en: "/support/my-bag-is-delayed-or-damaged-what-should-i-do",
      sq: "/informacion/bagazhi-im-eshte-vonuar-ose-demtuar-cfare-te-bej",
      de: "/verordnung/mein-gepäck-ist-verspätet-oder-beschädigt-was-soll-ich-tun",
      fr: "/assistance/mon-bagage-est-retardé-ou-endommagé-que-faire",
    },
  },
  baggagePurchaseOnline: {
    component: "BaggagePurchaseOnlinePage",
    routes: {
      en: "/support/can-i-purchase-baggage-at-the-airport-or-is-it-only-available-online",
      sq: "/informacion/a-mund-te-blej-bagazh-ne-aeroport-apo-vetem-online",
      de: "/verordnung/kann-ich-gepäck-am-flughafen-kaufen-oder-nur-online",
      fr: "/assistance/puis-je-acheter-des-bagages-à-laéroport-ou-uniquement-en-ligne",
    },
  },
  baggageThroughCheck: {
    component: "BaggageThroughCheckPage",
    routes: {
      en: "/support/is-my-bag-checked-in-to-my-final-destination",
      sq: "/informacion/a-dergohet-bagazhi-im-deri-ne-destinacionin-final",
      de: "/verordnung/wird-mein-gepäck-bis-zum-endziel-durchgecheckt",
      fr: "/assistance/mon-bagage-est-il-enregistré-jusquà-ma-destination-finale",
    },
  },
  bicycle: {
    component: "BicyclePage",
    routes: {
      en: "/support/i-bring-a-bike-on-my-flight",
      sq: "/informacion/a-mund-te-marr-biciklete-ne-fluturim",
      de: "/verordnung/darf-ich-ein-fahrrad-auf-meinen-flug-mitnehmen",
      fr: "/assistance/puis-je-emporter-un-vélo-sur-mon-vol",
    },
  },
  cpapPoc: {
    component: "CpapPocPage",
    routes: {
      en: "/support/can-i-bring-my-cpap-or-oxygen-device-on-board",
      sq: "/informacion/a-mund-te-marr-me-vete-pajisjen-cpap-ose-oksigjenin-ne-bord",
      de: "/verordnung/darf-ich-mein-cpap-oder-sauerstoffgerät-an-bord-mitnehmen",
      fr: "/assistance/puis-je-apporter-mon-appareil-cpap-ou-mon-dispositif-doxygène-à-bord",
    },
  },
  droneRules: {
    component: "DroneRulesPage",
    routes: {
      en: "/support/bring-a-drone-on-my-flight",
      sq: "/support/bring-a-drone-on-my-flight",
      de: "/verordnung/darf-ich-eine-drohne-auf-meinen-flug-mitnehmen",
      fr: "/assistance/puis-je-emporter-un-drone-sur-mon-vol",
    },
  },
  huntingGear: {
    component: "HuntingGearPage",
    routes: {
      en: "/support/travel-with-hunting-gear-like-guns-and-fishing-rods",
      sq: "/informacion/a-mund-te-udhetoj-me-pajisje-gjuetie-si-arme-ose-shufra-peshkimi",
      de: "/verordnung/kann-ich-mit-jagdausrüstung-wie-waffen-oder-angelruten-reisen",
      fr: "/assistance/puis-je-voyager-avec-du-matériel-de-chasse-comme-des-armes-ou-des-cannes-à-pêche",
    },
  },
  liquidBan: {
    component: "LiquidBanPage",
    routes: {
      en: "/support/how-does-the-liquid-ban-work",
      sq: "/informacion/si-funksionon-ndalimi-per-lengjet",
      de: "/verordnung/wie-funktioniert-das-flüssigkeitsverbot",
      fr: "/assistance/comment-fonctionne-linterdiction-des-liquides",
    },
  },
  lostFound: {
    component: "LostFoundPage",
    routes: {
      en: "/support/forgot-something-on-board-heres-what-to-do",
      sq: "/informacion/keni-harruar-dicka-ne-avion-ja-cfare-te-beni",
      de: "/verordnung/etwas-an-bord-vergessen-so-gehst-du-vor",
      fr: "/assistance/oublié-quelque-chose-à-bord-voici-quoi-faire",
    },
  },
  musicInstruments: {
    component: "MusicInstrumentsPage",
    routes: {
      en: "/support/travel-with-musical-instruments",
      sq: "/informacion/a-mund-te-udhetoj-me-instrumente-muzikore",
      de: "/verordnung/kann-ich-mit-musikinstrumenten-reisen",
      fr: "/assistance/puis-je-voyager-avec-des-instruments-de-musique",
    },
  },
  oversizedPacking: {
    component: "OversizedPackingPage",
    routes: {
      en: "/support/traveling-with-oversized-and-unusual-items-of-baggage",
      sq: "/informacion/udhetimi-me-bagazhe-te-medha-dhe-te-pazakonshme",
      de: "/verordnung/reisen-mit-übergroßem-oder-ungewöhnlichem-gepäck",
      fr: "/assistance/voyager-avec-des-bagages-volumineux-ou-inhabituels",
    },
  },
  oxygenCanisters: {
    component: "OxygenCanistersPage",
    routes: {
      en: "/support/how-can-i-travel-with-a-canister-of-oxygen",
      sq: "/informacion/si-te-udhetoj-me-bombol-oksigjeni",
      de: "/verordnung/wie-kann-ich-mit-einer-sauerstoffflasche-reisen",
      fr: "/assistance/comment-voyager-avec-une-bouteille-doxygène",
    },
  },
  prohibitedItems: {
    component: "ProhibitedItemsPage",
    routes: {
      en: "/support/which-items-are-prohibited-on-board",
      sq: "/informacion/cilat-sende-jane-te-ndaluara-ne-bord",
      de: "/verordnung/welche-gegenstände-sind-an-bord-verboten",
      fr: "/assistance/quels-objets-sont-interdits-à-bord",
    },
  },
  specialEquipmentCost: {
    component: "SpecialEquipmentCostPage",
    routes: {
      en: "/support/how-much-does-it-cost-to-travel-with-special-equipment",
      sq: "/informacion/sa-kushton-udhetimi-me-pajisje-te-vecanta",
      de: "/verordnung/wie-viel-kostet-es-mit-spezialausrüstung-zu-reisen",
      fr: "/assistance/combien-coûte-le-transport-de-matériel-spécial",
    },
  },
  sportsEquipment: {
    component: "SportsEquipmentPage",
    routes: {
      en: "/support/travel-with-sports-equipment",
      sq: "/informacion/a-mund-te-udhetoj-me-pajisje-sportive",
      de: "/verordnung/kann-ich-mit-sportausrüstung-reisen",
      fr: "/assistance/puis-je-voyager-avec-du-matériel-de-sport",
    },
  },

  changeCancelBooking: {
    component: "ChangeCancelBooking",
    routes: {
      en: "/support/how-do-i-change-or-cancel-a-booking",
      sq: "/informacion/si-te-ndryshoj-ose-anuloj-nje-rezervim",
      de: "/verordnung/wie-ändere-oder-storniere-ich-eine-buchung",
      fr: "/assistance/comment-modifier-ou-annuler-une-réservation",
    },
  },
  checkinOnline: {
    component: "CheckinOnline",
    routes: {
      en: "/support/how-do-i-check-in-online",
      sq: "/informacion/si-te-bej-check-in-online",
      de: "/verordnung/wie-kann-ich-online-einchecken",
      fr: "/assistance/comment-faire-mon-enregistrement-en-ligne",
    },
  },
  flyingWithAnimals: {
    component: "FlyingWithAnimalsPage",
    routes: {
      en: "/support/flying-with-animals-on-airtiketa",
      sq: "/informacion/udhetimi-me-kafshe-ne-airtiketa",
      de: "/verordnung/fliegen-mit-tieren-bei-airtiketa",
      fr: "/assistance/voyager-avec-des-animaux-avec-airtiketa",
    },
  },
  travelDocuments: {
    component: "TravelDocumentsPage",
    routes: {
      en: "/support/required-travel-documents",
      sq: "/informacion/dokumentet-e-nevojshme-per-udhetim",
      de: "/verordnung/erforderliche-reisedokumente",
      fr: "/assistance/documents-de-voyage-requis",
    },
  },

  AllergiesSupportPage: {
    component: "AllergiesSupportPage",
    routes: {
      en: "/support/i-have-allergies-can-i-fly-with-airtiketa",
      sq: "/informacion/kam-alergji-a-mund-te-udhetoj-me-airtiketa",
      de: "/verordnung/ich-habe-allergien-kann-ich-mit-airtiketa-fliegen",
      fr: "/assistance/jai-des-allergies-puis-je-voyager-avec-airtiketa",
    },
  },

  CastTravelPage: {
    component: "CastTravelPage",
    routes: {
      en: "/support/passengers-traveling-with-a-cast",
      sq: "/informacion/pasagjeret-qe-udhetojne-me-allci",
      de: "/verordnung/passagiere-die-mit-gips-reisen",
      fr: "/assistance/passagers-voyageant-avec-un-plâtre",
    },
  },

  ChangePassengerPage: {
    component: "ChangePassengerPage",
    routes: {
      en: "/support/can-i-change-a-passenger-in-a-booking",
      sq: "/informacion/a-mund-te-ndryshoj-nje-pasagjer-ne-nje-rezervim",
      de: "/verordnung/kann-ich-einen-passagier-in-einer-buchung-ändern",
      fr: "/assistance/puis-je-modifier-un-passager-dans-une-réservation",
    },
  },

  GroupBookingsPage: {
    component: "GroupBookingsPage",
    routes: {
      en: "/support/group-bookings-for-10-passengers",
      sq: "/informacion/rezervime-grupi-per-10-pasagjere",
      de: "/verordnung/gruppenbuchungen-für-10-oder-mehr-passagiere",
      fr: "/assistance/réservations-de-groupe-pour-10-passagers-ou-plus",
    },
  },

  HiddenDisabilityDPNAPage: {
    component: "HiddenDisabilityDPNAPage",
    routes: {
      en: "/support/i-have-a-hidden-disability-can-i-get-assistance-at-the-airport",
      sq: "/informacion/kam-nje-aftesi-te-kufizuar-te-padukshme-a-mund-te-marr-ndihme-ne-aeroport",
      de: "/verordnung/ich-habe-eine-nicht-sichtbare-behinderung-kann-ich-am-flughafen-unterstützung-bekommen",
      fr: "/assistance/jai-un-handicap-invisible-puis-je-obtenir-de-laide-à-laéroport",
    },
  },

  InvoiceRequestPage: {
    component: "InvoiceRequestPage",
    routes: {
      en: "/support/how-do-i-get-an-invoice",
      sq: "/informacion/si-mund-te-marr-nje-fature",
      de: "/verordnung/wie-erhalte-ich-eine-rechnung",
      fr: "/assistance/comment-obtenir-une-facture",
    },
  },

  MedicationOnBoardPage: {
    component: "MedicationOnBoardPage",
    routes: {
      en: "/support/what-are-the-rules-about-bringing-medication-on-board",
      sq: "/informacion/cilat-jane-rregullat-per-te-sjelle-ilace-ne-bord",
      de: "/verordnung/welche-regeln-gelten-für-die-mitnahme-von-medikamenten-an-bord",
      fr: "/assistance/quelles-sont-les-règles-concernant-les-médicaments-à-bord",
    },
  },

  MobilityAidsPage: {
    component: "MobilityAidsPage",
    routes: {
      en: "/support/can-i-bring-my-cane-or-walking-frame-on-board",
      sq: "/informacion/a-mund-te-marr-bastun-paterica-ose-kornize-ecjeje-ne-bord",
      de: "/verordnung/darf-ich-meinen-gehstock-krücken-oder-gehrahmen-an-bord-mitnehmen",
      fr: "/assistance/puis-je-apporter-ma-canne-mes-béquilles-ou-mon-déambulateur-à-bord",
    },
  },

  RefundApplyPage: {
    component: "RefundApplyPage",
    routes: {
      en: "/support/how-do-i-apply-for-a-refund",
      sq: "/informacion/si-te-aplikoj-per-rimbursim",
      de: "/verordnung/wie-beantrage-ich-eine-rückerstattung",
      fr: "/assistance/comment-demander-un-remboursement",
    },
  },

  SafetyAssistantPage: {
    component: "SafetyAssistantPage",
    routes: {
      en: "/support/traveling-with-a-safety-assistant",
      sq: "/informacion/udhetimi-me-nje-asistent-sigurie",
      de: "/verordnung/reisen-mit-einem-sicherheitsassistenten",
      fr: "/assistance/voyager-avec-un-assistant-de-sécurité",
    },
  },

  SpecialAssistanceWheelchairPage: {
    component: "SpecialAssistanceWheelchairPage",
    routes: {
      en: "/support/traveling-with-a-wheelchair",
      sq: "/informacion/udhetimi-me-karroce-invalidi",
      de: "/verordnung/reisen-mit-einem-rollstuhl",
      fr: "/assistance/voyager-avec-un-fauteuil-roulant",
    },
  },

  SpecialSeatingPage: {
    component: "SpecialSeatingPage",
    routes: {
      en: "/support/special-assistance-and-seating",
      sq: "/informacion/asistenca-speciale-dhe-uleset",
      de: "/verordnung/besondere-unterstützung-und-sitzplätze",
      fr: "/assistance/assistance-spéciale-et-sièges",
    },
  },
  RequestWheelchairPage: {
    component: "RequestWheelchairPage",
    routes: {
      en: "/support/how-do-i-request-wheelchair-assistance-at-the-airport",
      sq: "/informacion/si-te-kerkoj-ndihme-me-karroce-ne-aeroport",
      de: "/verordnung/wie-beantrage-ich-rollstuhlunterstützung-am-flughafen",
      fr: "/assistance/comment-demander-une-assistance-en-fauteuil-roulant-à-laéroport",
    },
  },
};

export function detectLanguageFromPath(pathname: string): SupportedLanguage {
  for (const [lang, prefix] of Object.entries(LANGUAGE_PREFIXES)) {
    if (pathname.startsWith(prefix)) {
      return lang as SupportedLanguage;
    }
  }
  return "en";
}

export function detectPageFromPath(pathname: string): string | null {
  for (const [pageKey, pageData] of Object.entries(PAGE_ROUTES)) {
    for (const route of Object.values(pageData.routes)) {
      if (pathname === route) {
        return pageKey;
      }
    }
  }
  return null;
}

export function getRouteForLanguage(
  pageKey: string,
  language: SupportedLanguage
): string | null {
  const pageData = PAGE_ROUTES[pageKey];
  if (!pageData) return null;
  return pageData.routes[language] || null;
}

export function getAllAlternateLinks(
  pageKey: string
): Array<{ lang: string; path: string }> {
  const pageData = PAGE_ROUTES[pageKey];
  if (!pageData) return [];
  return Object.entries(pageData.routes).map(([lang, path]) => ({
    lang,
    path,
  }));
}
